import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ProTable } from '@ant-design/pro-components';
import { App, Button, Popconfirm } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import AgentFeishuCollectionForm from '../components/AgentFeishuCollectionForm';
import AgentFeishuContactCollectionForm from '../components/AgentFeishuContactCollectionForm';
import AgentFeishuStoreCollectionForm from '../components/AgentFeishuStoreCollectionForm';
import AgentHoseCollectionForm from '../components/AgentHoseCollectionForm';
import {
  FEISHU_BITABLE_COLLECTION_TYPE,
  FEISHU_BITABLE_EXPENSE_COLLECTION_TYPE,
  FEISHU_BITABLE_GROSS_MARGIN_COLLECTION_TYPE,
  FEISHU_BITABLE_SALARY_COLLECTION_TYPE,
  FEISHU_BITABLE_STORE_COLLECTION_TYPE,
  FEISHU_CONTACT_COLLECTION_TYPE,
  HOSE_EXPENSE_COLLECTION_TYPE,
  HOSE_LOAN_COLLECTION_TYPE,
  HOSE_PAYMENT_COLLECTION_TYPE,
  HOSE_REQUISITION_COLLECTION_TYPE,
  queryCollections,
  queryConnections,
  removeCollection,
} from '../services/collect';
import { getCollectionAgentDefinition } from '../utils/collection-agent';
import { formatCollectionLastCompletedAt } from '../utils/collection-time';

const COLLECTION_FORMS = {
  [FEISHU_BITABLE_COLLECTION_TYPE]: AgentFeishuCollectionForm,
  [FEISHU_BITABLE_EXPENSE_COLLECTION_TYPE]: AgentFeishuCollectionForm,
  [FEISHU_BITABLE_GROSS_MARGIN_COLLECTION_TYPE]: AgentFeishuCollectionForm,
  [FEISHU_BITABLE_SALARY_COLLECTION_TYPE]: AgentFeishuCollectionForm,
  [FEISHU_BITABLE_STORE_COLLECTION_TYPE]: AgentFeishuStoreCollectionForm,
  [FEISHU_CONTACT_COLLECTION_TYPE]: AgentFeishuContactCollectionForm,
  [HOSE_EXPENSE_COLLECTION_TYPE]: AgentHoseCollectionForm,
  [HOSE_LOAN_COLLECTION_TYPE]: AgentHoseCollectionForm,
  [HOSE_PAYMENT_COLLECTION_TYPE]: AgentHoseCollectionForm,
  [HOSE_REQUISITION_COLLECTION_TYPE]: AgentHoseCollectionForm,
};

const AgentCollectionConfig = ({ agent, organizationId }) => {
  const collectionActionRef = useRef(null);
  const [connections, setConnections] = useState([]);
  const { message } = App.useApp();
  const definition = getCollectionAgentDefinition(agent.configuration);
  const CollectionForm = COLLECTION_FORMS[definition.collectionType];
  const reload = () => collectionActionRef.current?.reload?.();

  const collectionColumns = useMemo(
    () => [
      { title: '采集配置', dataIndex: 'name' },
      { title: definition.connectionLabel, dataIndex: 'connectionName' },
      {
        title: '最后采集',
        dataIndex: ['cursor', 'lastCompletedAt'],
        renderText: formatCollectionLastCompletedAt,
      },
      {
        title: '操作',
        valueType: 'option',
        render: (_, row) => [
          <CollectionForm
            key="edit"
            collectionType={agent.configuration.collectionType}
            connections={connections}
            dateMappingMode={definition.dateMappingMode}
            eventDomain={definition.eventDomain}
            organizationId={organizationId}
            record={row}
            onSaved={reload}
            trigger={
              <Button type="link" icon={<EditOutlined />}>
                修改
              </Button>
            }
          />,
          <Popconfirm
            key="delete"
            title="删除采集配置"
            onConfirm={async () => {
              await removeCollection({ data: { id: row.id, organizationId } });
              message.success('采集配置已删除');
              reload();
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>,
        ],
      },
    ],
    [
      agent.configuration.collectionType,
      connections,
      definition.connectionLabel,
      definition.dateMappingMode,
      definition.eventDomain,
      message,
      organizationId,
    ],
  );

  return (
    <ProTable
      actionRef={collectionActionRef}
      rowKey="id"
      size="small"
      options={false}
      columns={collectionColumns}
      search={false}
      request={async (params) => {
        const [connectionResult, collectionResult] = await Promise.all([
          queryConnections({
            connectionType: agent.configuration.connectionType,
            organizationId,
            pageSize: 100,
          }),
          queryCollections({
            ...params,
            collectionType: agent.configuration.collectionType,
            organizationId,
          }),
        ]);
        setConnections(connectionResult.data);
        return collectionResult;
      }}
      toolBarRender={() => [
        <CollectionForm
          key="create"
          collectionType={agent.configuration.collectionType}
          connections={connections}
          dateMappingMode={definition.dateMappingMode}
          eventDomain={definition.eventDomain}
          organizationId={organizationId}
          onSaved={reload}
          trigger={
            <Button type="primary" icon={<PlusOutlined />}>
              创建采集配置
            </Button>
          }
        />,
      ]}
    />
  );
};

AgentCollectionConfig.propTypes = {
  agent: PropTypes.shape({
    agentId: PropTypes.string.isRequired,
    configuration: PropTypes.shape({
      collectionType: PropTypes.string.isRequired,
      connectionType: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  organizationId: PropTypes.string.isRequired,
};

export default AgentCollectionConfig;
